/**
 * StatusDistributionChart Component
 * Donut chart showing OKR status distribution
 *
 * Story 2.2: OKR Status Distribution Pie Chart
 */

import React, { useState, useCallback } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Sector,
} from 'recharts';
import { clsx } from 'clsx';
import { Card } from '../../common';
import { STATUS_CONFIG } from './okrStatusChartColors';

export { STATUS_CONFIG };

export interface StatusDistributionItem {
  status: string;
  count: number;
  percentage: number;
}

export interface StatusDistributionChartProps {
  /** Status distribution data */
  data: StatusDistributionItem[];
  /** Title for the chart */
  title?: string;
  /** Whether to show the total in center */
  showTotal?: boolean;
  /** Click handler for status segment */
  onStatusClick?: (status: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Custom active shape for hover effect
 */
const renderActiveShape = (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 10}
        outerRadius={outerRadius + 14}
        fill={fill}
      />
      <text x={cx} y={cy - 10} textAnchor="middle" fill="#1c1917" fontSize="24" fontWeight="bold">
        {payload.count}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#78716c" fontSize="12">
        {payload.label} ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
};

/**
 * Custom tooltip content
 */
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-stone-200">
        <div className="flex items-center gap-2 mb-1">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: STATUS_CONFIG[data.status]?.color || '#78716c' }}
          />
          <span className="font-medium text-stone-900">{data.label}</span>
        </div>
        <div className="text-sm text-stone-600">
          {data.count} OKRs ({data.percentage}%)
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Custom legend content
 */
const CustomLegend = ({
  payload,
  onStatusClick,
}: {
  payload: any[];
  onStatusClick?: (status: string) => void;
}) => {
  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
      {payload.map((entry, index) => (
        <button
          key={index}
          onClick={() => onStatusClick?.(entry.payload.status)}
          className={clsx(
            'flex items-center gap-1.5 px-2 py-1 rounded text-sm transition-colors',
            onStatusClick ? 'hover:bg-stone-100 cursor-pointer' : 'cursor-default'
          )}
        >
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-stone-600">{entry.payload.label}</span>
          <span className="text-stone-400">({entry.payload.count})</span>
        </button>
      ))}
    </div>
  );
};

/**
 * Loading skeleton for the chart
 */
const ChartSkeleton = () => (
  <div className="flex flex-col items-center justify-center py-8">
    <div className="w-48 h-48 rounded-full bg-stone-200 animate-pulse flex items-center justify-center">
      <div className="w-24 h-24 rounded-full bg-white" />
    </div>
    <div className="flex gap-4 mt-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-stone-200 animate-pulse" />
          <div className="h-3 w-12 bg-stone-200 animate-pulse rounded" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Status Distribution Donut Chart Component
 */
export const StatusDistributionChart: React.FC<StatusDistributionChartProps> = ({
  data,
  title = 'OKR Status Distribution',
  showTotal = true,
  onStatusClick,
  isLoading = false,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const onPieEnter = useCallback((_: any, index: number) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(undefined);
  }, []);

  // Prepare chart data with labels and colors
  const chartData = data.map((item) => ({
    ...item,
    label: STATUS_CONFIG[item.status]?.label || item.status,
    color: STATUS_CONFIG[item.status]?.color || '#78716c',
  }));

  const total = chartData.reduce((sum, item) => sum + item.count, 0);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <ChartSkeleton />
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <div className="flex flex-col items-center justify-center py-12 text-stone-500">
          <p>No data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>

      <div className="h-72 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="45%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="count"
              activeIndex={activeIndex}
              activeShape={showTotal ? renderActiveShape : undefined}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              onClick={(data) => onStatusClick?.(data.status)}
              style={{ cursor: onStatusClick ? 'pointer' : 'default' }}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            {!showTotal && <Tooltip content={<CustomTooltip />} />}
            <Legend
              content={(props) => (
                <CustomLegend
                  payload={props.payload || []}
                  onStatusClick={onStatusClick}
                />
              )}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Center total (when not using active shape) */}
        {showTotal && activeIndex === undefined && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ marginTop: '-20px' }}>
            <div className="text-center">
              <div className="text-3xl font-bold text-stone-900">{total}</div>
              <div className="text-sm text-stone-500">Total OKRs</div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default StatusDistributionChart;
